// =============================================================================
// hestia/ordre.js : l'ordre de toute liste d'Hestia.
// -----------------------------------------------------------------------------
// Décision du DG d'Enio (« aucun point, aucun classement ») : un ordre de liste est
// CANONIQUE (objectifs 1 à 7, étapes d'un circuit) ou ALPHABÉTIQUE (personnes
// par NOM puis prénom, mutuelles par libellé), jamais un ordre de mérite, de
// volume ou d'ancienneté d'engagement. Toute liste d'Hestia passe par ici
// (src/engagementSansClassement.test.js l'exige des fichiers Hestia).
// Module pur.
// =============================================================================

const COLLATION = new Intl.Collator("fr", { sensitivity: "base", numeric: true, ignorePunctuation: true });
const texte = (v) => String(v == null ? "" : v).trim();

/** « Prénom NOM » d'une fiche (la fiche porte déjà la graphie de la règle
 *  « Prénom NOM », src/nomsPersonnes.js ; on ne la refait pas ici). */
export function nomAffiche(p) {
  if (!p) return "";
  const n = [texte(p.prenom), texte(p.nom)].filter(Boolean).join(" ");
  return n || texte(p.nomAffiche) || "Personne sans nom";
}

/** « de » devant un nom, élidé devant une voyelle : « à la place d'Élise
 *  JAMBERT », « à la place de Thierry BELCASTEL » (relecture H1, tour 3).
 *  Le h et le y ne s'élident pas ici (h aspiré, y consonne : on garde « de »). */
export function deNom(nom) {
  const n = texte(nom);
  return /^[aeiou]/i.test(n.normalize("NFD")) ? `d'${n}` : `de ${n}`;
}

/** Deux noms qui se lisent pareil : même collation que l'ordre alphabétique
 *  (sensibilité « base », ponctuation ignorée, chiffres en nombres). Elle
 *  ignore la casse, les accents, la ponctuation ET les espaces (« Jean Marc »
 *  = « JeanMarc »), égale ß et ss, œ et oe, « 1 » et « 01 » : des noms égaux
 *  en ce sens se lisent de la même façon (relecture finale de H1, 04/10 ;
 *  rien d'exploitable, le commentaire en disait moins). Règle « Prénom NOM »
 *  (CLAUDE.md) : toute comparaison avec une copie dénormalisée d'un nom se
 *  fait sans la casse ; sinon l'harmonisation des noms ferait signaler un faux
 *  « nom changé » sur chaque demande en cours (relecture du 04/10 au soir). */
export const memeNom = (a, b) => COLLATION.compare(texte(a), texte(b)) === 0;

/** Comparaison alphabétique de deux personnes : NOM, puis prénom, puis
 *  identifiant (un ordre total et stable, jamais un rang). */
export function comparerPersonnes(a, b) {
  return COLLATION.compare(texte(a && a.nom), texte(b && b.nom))
    || COLLATION.compare(texte(a && a.prenom), texte(b && b.prenom))
    || COLLATION.compare(texte(a && a.id), texte(b && b.id));
}

/** Copie triée par ordre alphabétique des personnes. */
export const trierPersonnes = (liste) => (Array.isArray(liste) ? [...liste] : []).sort(comparerPersonnes);

/** Copie triée par libellé (mutuelles, projets) : alphabétique, identifiant en départage. */
export function trierParLibelle(liste, cle = (x) => x.libelle) {
  return (Array.isArray(liste) ? [...liste] : []).sort((a, b) =>
    COLLATION.compare(texte(cle(a)), texte(cle(b))) || COLLATION.compare(texte(a && a.id), texte(b && b.id)));
}

/** Copie triée dans l'ordre CANONIQUE des objectifs (1 à 7). */
export const trierObjectifs = (liste, cle = (x) => x.n) =>
  (Array.isArray(liste) ? [...liste] : []).sort((a, b) => Number(cle(a)) - Number(cle(b)));
